/**
 * ConversationKit.js
 *
 * Deep-blue "conversation" surface shared by the AI assistant and Team chat
 * (the one canvas where the blueprint allows non-orange; orange stays the
 * accent): backdrop, pearl orb, prompt tiles, glass bubbles and the composer
 * bar, plus the dashboard's "Ask anything" entry card.
 *
 * Presentation only — callers own messages, sending and navigation.
 *
 * @module components/premium/ConversationKit
 */

import React from 'react';
import {ActivityIndicator, Pressable, StyleSheet, Text, TextInput, View} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import Svg, {Circle, Defs, RadialGradient, Stop} from 'react-native-svg';
import Icon from '../management/SafeIcon';
import {BRAND, CONVERSATION} from '../../helpers/managementTheme';

/**
 * Full-bleed deep-blue gradient with two soft glow blobs. Render it as the first
 * child of a screen (it is `absoluteFill`, non-interactive).
 *
 * @returns {JSX.Element} The backdrop.
 */
export const ConversationBackground = () => (
    <View pointerEvents="none" style={StyleSheet.absoluteFill}>
        <LinearGradient colors={CONVERSATION.gradient} locations={CONVERSATION.locations} start={{x: 0.2, y: 0}} end={{x: 0.8, y: 1}} style={StyleSheet.absoluteFill} />
        <View style={styles.glowTop} />
        <View style={styles.glowSide} />
    </View>
);

/**
 * Pearl orb — the assistant's avatar (radial blue-white sphere).
 *
 * @param {object} props
 * @param {number} [props.size=34] Diameter in px.
 * @param {object} [props.style] Extra container style.
 * @returns {JSX.Element} The orb.
 */
export const OrbAvatar = ({size = 34, style}) => (
    <View style={[{width: size, height: size}, style]}>
        <Svg width={size} height={size} viewBox="0 0 100 100">
            <Defs>
                <RadialGradient id="orb" cx="34%" cy="28%" rx="78%" ry="78%" fx="34%" fy="28%">
                    <Stop offset="0" stopColor="#FFFFFF" stopOpacity={1} />
                    <Stop offset="0.38" stopColor="#B9CCFF" stopOpacity={1} />
                    <Stop offset="1" stopColor="#3B63E8" stopOpacity={1} />
                </RadialGradient>
            </Defs>
            <Circle cx="50" cy="50" r="48" fill="url(#orb)" />
        </Svg>
    </View>
);

/**
 * Dark translucent prompt tile (icon on top, label bottom-left) — the 2×2 grid
 * of the "How can I help you today?" start screen.
 *
 * @param {object} props
 * @param {string} props.icon MDI glyph.
 * @param {string} props.label Tile label.
 * @param {Function} props.onPress Tap handler.
 * @param {string|number} [props.width='48.5%'] Tile width.
 * @returns {JSX.Element} The tile.
 */
export const PromptTile = ({icon, label, onPress, width = '48.5%'}) => (
    <Pressable
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={label}
        style={({pressed}) => [styles.promptTile, {width, opacity: pressed ? 0.82 : 1}]}
    >
        <View style={styles.promptIcon}>
            <Icon name={icon} size={16} color="#FFFFFF" />
        </View>
        <Text style={styles.promptLabel} numberOfLines={2}>{label}</Text>
    </Pressable>
);

/**
 * Chat bubble. `own` bubbles are right-aligned translucent white, the other
 * side is a darker glass bubble preceded by the orb.
 *
 * @param {object} props
 * @param {boolean} [props.own=false] Right-aligned (the current user).
 * @param {boolean} [props.showOrb=false] Show the assistant orb beside a non-own bubble.
 * @param {React.ReactNode} props.children Bubble content.
 * @param {object} [props.style] Extra bubble style.
 * @returns {JSX.Element} The bubble.
 */
export const GlassBubble = ({own = false, showOrb = false, children, style}) => (
    <View style={[styles.bubbleRow, own ? styles.bubbleRowOwn : styles.bubbleRowOther]}>
        {!own && showOrb && <OrbAvatar size={28} style={styles.bubbleOrb} />}
        <View style={[styles.bubble, own ? styles.bubbleOwn : styles.bubbleOther, style]}>{children}</View>
    </View>
);

/**
 * Composer bar: orb, text input and a round brand-orange send button.
 *
 * @param {object} props
 * @param {string} props.value Input value.
 * @param {Function} props.onChangeText Input change handler.
 * @param {Function} props.onSend Send handler.
 * @param {string} [props.placeholder] Placeholder text.
 * @param {boolean} [props.sending=false] Show a spinner instead of the send glyph and lock input.
 * @param {boolean} [props.multiline=true] Allow the input to grow.
 * @param {boolean} [props.lockWhileSending=true] Make the input read-only while `sending` (set false to keep typing during a turn).
 * @param {string} [props.sendLabel] Accessibility label of the send button.
 * @param {React.ReactNode} [props.leading] Replaces the orb (e.g. an attach button).
 * @param {React.ReactNode} [props.header] Rendered above the input row (mode chips…).
 * @param {object} [props.style] Extra container style.
 * @returns {JSX.Element} The composer.
 */
export const AskBar = ({value, onChangeText, onSend, placeholder, sending = false, multiline = true, lockWhileSending = true, sendLabel, leading, header, style}) => {
    const canSend = !!String(value || '').trim() && !sending;

    return (
        <View style={[styles.ask, style]}>
            {header}
            <View style={styles.askRow}>
                {leading || <OrbAvatar size={30} />}
                <TextInput
                    value={value}
                    onChangeText={onChangeText}
                    placeholder={placeholder}
                    placeholderTextColor="rgba(214,226,255,0.5)"
                    style={styles.askInput}
                    multiline={multiline}
                    editable={!(sending && lockWhileSending)}
                    onSubmitEditing={onSend}
                    selectionColor={BRAND.light}
                />
                <Pressable
                    onPress={onSend}
                    disabled={!canSend}
                    accessibilityRole="button"
                    accessibilityLabel={sendLabel}
                    style={[styles.send, canSend ? styles.sendActive : styles.sendIdle]}
                >
                    {sending
                        ? <ActivityIndicator size="small" color="#FFFFFF" />
                        : <Icon name="arrow-up" size={20} color={canSend ? '#FFFFFF' : 'rgba(255,255,255,0.45)'} />}
                </Pressable>
            </View>
        </View>
    );
};

/**
 * Dashboard entry card: deep-blue gradient, orb, headline, an input-looking pill
 * (tap opens the assistant) and an optional Team chat chip with its unread count.
 *
 * @param {object} props
 * @param {string} props.title Headline.
 * @param {string} props.placeholder Text inside the pill.
 * @param {Function} [props.onAsk] Opens the assistant (hides the pill when omitted).
 * @param {string} [props.chatLabel] Team chat chip label (hides the chip when omitted).
 * @param {number} [props.chatBadge] Unread count on the chip.
 * @param {Function} [props.onChat] Opens Team chat.
 * @returns {JSX.Element} The card.
 */
export const AskAiCard = ({title, placeholder, onAsk, chatLabel, chatBadge = 0, onChat}) => (
    <LinearGradient colors={['#1B7FE0', '#0B3F96', '#06255E']} start={{x: 0, y: 0}} end={{x: 1, y: 1}} style={styles.card}>
        <View pointerEvents="none" style={styles.cardGlow} />
        <View style={styles.cardTop}>
            <OrbAvatar size={36} />
            <Text style={styles.cardTitle} numberOfLines={2}>{title}</Text>
        </View>
        {!!onAsk && (
            <Pressable onPress={onAsk} accessibilityRole="button" accessibilityLabel={placeholder} style={({pressed}) => [styles.cardInput, pressed && {opacity: 0.88}]}>
                <Text style={styles.cardInputText} numberOfLines={1}>{placeholder}</Text>
                <View style={styles.cardSend}>
                    <Icon name="arrow-up" size={18} color="#FFFFFF" />
                </View>
            </Pressable>
        )}
        {!!chatLabel && (
            <Pressable onPress={onChat} accessibilityRole="button" style={({pressed}) => [styles.chatChip, pressed && {opacity: 0.85}]}>
                <Icon name="forum-outline" size={16} color="#FFFFFF" />
                <Text style={styles.chatChipText} numberOfLines={1}>{chatLabel}</Text>
                {chatBadge > 0 && (
                    <View style={styles.chatChipBadge}>
                        <Text style={styles.chatChipBadgeText}>{chatBadge > 99 ? '99+' : chatBadge}</Text>
                    </View>
                )}
                <Icon name="chevron-right" size={16} color="rgba(255,255,255,0.8)" />
            </Pressable>
        )}
    </LinearGradient>
);

const styles = StyleSheet.create({
    glowTop: {position: 'absolute', width: 420, height: 420, borderRadius: 210, backgroundColor: 'rgba(56,150,255,0.28)', top: -230, left: -120},
    glowSide: {position: 'absolute', width: 300, height: 300, borderRadius: 150, backgroundColor: 'rgba(249,115,22,0.07)', bottom: 120, right: -160},

    promptTile: {borderRadius: 22, padding: 14, minHeight: 104, justifyContent: 'space-between', backgroundColor: CONVERSATION.surface, borderWidth: 1, borderColor: CONVERSATION.border},
    promptIcon: {width: 34, height: 34, borderRadius: 17, backgroundColor: CONVERSATION.surfaceStrong, alignItems: 'center', justifyContent: 'center'},
    promptLabel: {color: '#FFFFFF', fontSize: 15, fontWeight: '700', marginTop: 14},

    bubbleRow: {flexDirection: 'row', alignItems: 'flex-end', marginTop: 12},
    bubbleRowOwn: {justifyContent: 'flex-end'},
    bubbleRowOther: {justifyContent: 'flex-start'},
    bubbleOrb: {marginRight: 8, marginBottom: 2},
    bubble: {maxWidth: '84%', borderRadius: 22, paddingVertical: 11, paddingHorizontal: 15},
    bubbleOwn: {backgroundColor: 'rgba(255,255,255,0.18)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.22)', borderBottomRightRadius: 8},
    bubbleOther: {backgroundColor: 'rgba(4,14,44,0.55)', borderWidth: 1, borderColor: CONVERSATION.border, borderBottomLeftRadius: 8},

    ask: {borderRadius: 28, backgroundColor: 'rgba(4,14,44,0.62)', borderWidth: 1, borderColor: CONVERSATION.border, paddingVertical: 8, paddingHorizontal: 10},
    askRow: {flexDirection: 'row', alignItems: 'flex-end', gap: 10, paddingVertical: 2},
    askInput: {flex: 1, color: '#FFFFFF', fontSize: 15.5, maxHeight: 120, paddingTop: 6, paddingBottom: 6, minHeight: 34},
    send: {width: 38, height: 38, borderRadius: 19, alignItems: 'center', justifyContent: 'center'},
    sendActive: {backgroundColor: BRAND.base},
    sendIdle: {backgroundColor: 'rgba(255,255,255,0.12)'},

    card: {borderRadius: 28, padding: 18, overflow: 'hidden', marginBottom: 16, shadowColor: '#06255E', shadowOffset: {width: 0, height: 14}, shadowOpacity: 0.3, shadowRadius: 22, elevation: 8},
    cardGlow: {position: 'absolute', width: 240, height: 240, borderRadius: 120, backgroundColor: 'rgba(120,190,255,0.22)', top: -110, right: -80},
    cardTop: {flexDirection: 'row', alignItems: 'center', gap: 12},
    cardTitle: {flex: 1, color: '#FFFFFF', fontSize: 20, fontWeight: '800', letterSpacing: -0.4},
    cardInput: {flexDirection: 'row', alignItems: 'center', marginTop: 16, borderRadius: 999, backgroundColor: 'rgba(4,14,44,0.5)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.18)', paddingLeft: 18, paddingRight: 6, height: 50},
    cardInputText: {flex: 1, color: 'rgba(226,236,255,0.72)', fontSize: 14.5, fontWeight: '500'},
    cardSend: {width: 38, height: 38, borderRadius: 19, backgroundColor: BRAND.base, alignItems: 'center', justifyContent: 'center'},
    chatChip: {flexDirection: 'row', alignItems: 'center', gap: 8, alignSelf: 'flex-start', marginTop: 12, borderRadius: 999, backgroundColor: 'rgba(255,255,255,0.14)', paddingHorizontal: 14, height: 38},
    chatChipText: {color: '#FFFFFF', fontSize: 13.5, fontWeight: '700', flexShrink: 1},
    chatChipBadge: {minWidth: 20, height: 20, borderRadius: 10, backgroundColor: BRAND.base, paddingHorizontal: 5, alignItems: 'center', justifyContent: 'center'},
    chatChipBadgeText: {color: '#FFFFFF', fontSize: 11, fontWeight: '800'},
});
